/**
 * The data a Games snapshot page was rendered from, handed to the app that
 * replaces it (ADR-036).
 *
 * A snapshot is real markup; the app mounts over it with `createRoot()` and
 * would otherwise draw loading states until its own requests answer. So each
 * snapshot page also carries the data it was drawn from — in exactly the shape
 * the catalog API answers that page with — in one JSON element in `<head>`.
 * The app reads it once and starts its queries from it, so its first paint is
 * the snapshot's content, and it refreshes in the background.
 *
 * The handoff names the page it belongs to (`path`), so it can never seed
 * another page. It carries no timestamp on purpose: the publisher writes only
 * pages whose bytes changed, and a clock in every page would rewrite them all
 * on every refresh. The app treats the data as already stale instead.
 */
import type { GameArchePageData, GameDetailData, GameGenrePageData, GamesHomeData } from './types';

/** The `id` of the `<script type="application/json">` element in `<head>`. */
export const GAMES_SNAPSHOT_DATA_ELEMENT_ID = 'fias-games-data';

/** Bumped when the handoff's shape changes; the app ignores any other version. */
export const GAMES_SNAPSHOT_DATA_VERSION = 1;

/** How many games one page of a genre holds — the catalog API's page size. */
export const GAMES_GENRE_PAGE_SIZE = 60;

/** One page's data, as the catalog API answers it. */
export type GamesPageData =
  | { kind: 'home'; home: GamesHomeData }
  | { kind: 'genre'; genreId: string; pages: GameGenrePageData[] }
  | { kind: 'game'; slug: string; detail: GameDetailData }
  | { kind: 'arche'; key: string; page: GameArchePageData };

export interface GamesSnapshotHandoff {
  v: typeof GAMES_SNAPSHOT_DATA_VERSION;
  /** The page this data was drawn for: `/a/games`, `/a/games/genre/<id>`, … */
  path: string;
  data: GamesPageData;
}

/**
 * A genre's games as the API pages them ({@link GAMES_GENRE_PAGE_SIZE} at a
 * time), so a snapshot that shows every game seeds the app's paged list with
 * the same games in the same order.
 */
export function splitGenrePages(
  page: GameGenrePageData,
  size: number = GAMES_GENRE_PAGE_SIZE,
): GameGenrePageData[] {
  if (page.games.length === 0) return [page];
  const pages: GameGenrePageData[] = [];
  for (let start = 0; start < page.games.length; start += size) {
    pages.push({ ...page, games: page.games.slice(start, start + size) });
  }
  return pages;
}
